import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'

import { getAppExclusiveMessageEventProperties } from '@src/quince-components/src/atoms/greyBoxes/utils'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import ProductMessage from '@src/quince-components/src/atoms/productCTA/productMessage'
import { ProductCustomBannerMessage } from '@src/quince-components/src/atoms/productCustomBannerMessage'
import { documentToReactComponentsTextOptions } from '@src/quince-components/src/atoms/productForm'
import type { VariantPurchaseInfo } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { ProductOfferMessage } from '@src/quince-components/src/atoms/productOfferMessage'
import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { getIsSelectedColorAppExclusive } from '@src/quince-site-common/src/helpers/productHelper'
import * as svdCustomFields from '@src/quince-site-common/src/helpers/svd-custom-fields'
import { getOverriddenOfferMessage } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getFinalSaleEventProperties } from '@src/quince-site-common/src/utils/finalSaleEventUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { trackImpressions } from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import {
  appExclusiveLinkCss,
  appExclusiveMessageCss,
  mtoTextCss,
  mtoTextHiddenCss,
  mtoTextNewDesignV2Css,
  subDescTextBlackCss,
  subDescTextCss,
  subDescTextNewDesignV2Css,
  subDescTextSubTextCss,
} from './product-cta-ds.styles'

// @i18n-ignore
const QUINCE_APP = 'Quince App'

export interface GreyBoxesDSProps {
  product?: Product
  productOptions?: ProductOption[]
  selectedOptions?: ProductVariantOption[]
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  productCTAType: CTA_TYPE
  // SVD flow: read shipping-info / age-restriction / banner custom fields SVD-native. Threaded from
  // `product-form-ds` (main buy box); defaults false so miniPDP's direct render stays legacy.
  isSvd?: boolean
  isReturnable?: boolean
  subDescriptionText?: string
  hideOfferMessage?: boolean
  offerMessageFromPC?: string
  hideMtoText?: boolean
  preOrderModalData?: ContentfulPreOrderModal
  isFinalSaleProduct?: boolean
  isQuickATCModal?: boolean
  isPostOrderFlow?: boolean
  handleShopInApp: ({
    product,
    selectedOptions,
  }: {
    product?: Product
    selectedOptions?: ProductVariantOption[]
  }) => void
}

/**
 * DS twin of legacy `GreyBoxes` (full parity). Renders, in order:
 *   - the app-exclusive message (early return, with the `app_exclusive_message` IS_VISIBLE
 *     impression — unchanged, verified telemetry parity), OR
 *   - the MTO / shipping-info grey box (with the "not returnable" sub-text), the sub-description
 *     grey box, the offer message (`ProductOfferMessage` router -> DS twin here), the preorder
 *     `ProductMessage` (-> `PreOrderMessage`, DS-token gated for V2), and the custom-banner message
 *     (`ProductCustomBannerMessage` router -> DS twin here). The offer / custom-banner DS twins keep
 *     the legacy logic + telemetry and only swap the grey-box SCSS for Panda tokens.
 *
 * Business logic and telemetry are reused from the shared helpers (`getOverriddenOfferMessage`,
 * `getShippingInfoText`, `getAppExclusiveMessageEventProperties`, `documentToReactComponents` +
 * `documentToReactComponentsTextOptions`, `getSanitizedHtml`) — never reimplemented. Prop-compatible
 * with legacy `GreyBoxes` so it is a drop-in for the form shell.
 */
const GreyBoxesDS = (props: GreyBoxesDSProps) => {
  const { t } = useTranslation('common')
  const { productCTAType } = props
  const appExclusiveImpressionRef = useRef<HTMLDivElement>(null)
  const isColorAppExclusive = getIsSelectedColorAppExclusive(props.productOptions, props.selectedOptions)

  const offerMessage = getOverriddenOfferMessage(props.offerMessageFromPC, props.product, props.selectedOptions)

  const shouldShowMTOText =
    !props.hideMtoText &&
    props.selectedVariantPurchaseInfo?.showMadeToOrder &&
    props.selectedVariantPurchaseInfo.madeToOrderText

  const shippingInfoText =
    props.product &&
    (props.isSvd
      ? svdCustomFields.getSvdShippingInfoText(props.product)
      : productHelper.getShippingInfoText(props.product))

  const showCustomBannerMessage = !(props.isSvd
    ? svdCustomFields.getSvdIsAgeRestrictedProduct(props.product)
    : productHelper.getIsAgeRestrictedProduct(props.product as Product))

  const { promo_price } = getFinalSaleEventProperties(props.product ?? ({} as Product), props.selectedOptions ?? [])
  const propertyOverrides = {
    discounted_price: promo_price ?? props.product?.variants?.[0]?.price,
    price: props.product?.variants?.[0]?.price,
  }

  // Verbatim from legacy GreyBoxes — `app_exclusive_message` IS_VISIBLE impression.
  const onAppExclusiveImpression = () => {
    trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
      ...getAppExclusiveMessageEventProperties({
        product: props.product ?? ({} as Product),
        selectedOptions: props.selectedOptions ?? [],
        isFinalSaleProduct: props.isFinalSaleProduct ?? false,
        isColorAppExclusive,
        propertyOverrides,
        action: 'app_exclusive_message',
      }),
    })
  }
  useEffect(() => {
    if (appExclusiveImpressionRef.current && isColorAppExclusive && !props.isPostOrderFlow) {
      trackImpressions(appExclusiveImpressionRef.current, onAppExclusiveImpression, {}, {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parity with legacy GreyBoxes effect deps
  }, [isColorAppExclusive, props.isPostOrderFlow])

  if (isColorAppExclusive && !props.isPostOrderFlow) {
    return (
      <div className={appExclusiveMessageCss} ref={appExclusiveImpressionRef}>
        {t('app_exclusive_available')}
        {` `}
        <button
          type="button"
          className={appExclusiveLinkCss}
          onClick={() => {
            props.handleShopInApp({ product: props.product, selectedOptions: props.selectedOptions })
          }}
        >
          {QUINCE_APP}
        </button>
        {`. `}
        {t('app_exclusive_early_access')}
      </div>
    )
  }

  return (
    <>
      {shouldShowMTOText ||
      (!props.isReturnable &&
        !(props.isSvd
          ? svdCustomFields.getSvdIsAgeRestrictedProduct(props.product)
          : productHelper.getIsAgeRestrictedProduct(props.product as Product))) ? (
        <div
          className={cx(
            mtoTextCss,
            mtoTextNewDesignV2Css,
            !shouldShowMTOText && !shippingInfoText ? mtoTextHiddenCss : undefined,
          )}
        >
          {shouldShowMTOText ? (
            <>
              {props.selectedVariantPurchaseInfo?.madeToOrderText
                ? documentToReactComponents(
                    props.selectedVariantPurchaseInfo.madeToOrderText as unknown as Parameters<
                      typeof documentToReactComponents
                    >[0],
                    documentToReactComponentsTextOptions,
                  )
                : null}
            </>
          ) : (
            shippingInfoText && (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(shippingInfoText as string) }} />
            )
          )}
        </div>
      ) : null}
      {props.subDescriptionText ? (
        <div className={cx(subDescTextCss, subDescTextNewDesignV2Css)} data-testid="sub-description-text">
          <div
            // mutually exclusive — stacking both color classes lets Panda's source order win (umber), so
            // the returnable (black) case was rendering red. Pick one.
            className={props.isReturnable ? subDescTextBlackCss : subDescTextSubTextCss}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: getSanitizedHtml(props.subDescriptionText) }}
          />
        </div>
      ) : null}

      <ProductOfferMessage
        hideOfferMessage={props.hideOfferMessage}
        offerMessageFromPC={offerMessage}
        productId={props.product?.spid as number}
        showFinalSaleMessage={props.isFinalSaleProduct}
      />
      <ProductMessage
        type={productCTAType}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        preOrderModalData={props.preOrderModalData}
        isQuickATCModal={props.isQuickATCModal}
      />
      {showCustomBannerMessage ? (
        <ProductCustomBannerMessage
          customBannerMessageFromPC={
            props.isSvd
              ? svdCustomFields.getSvdPdpCustomBannerText(props.product)
              : productHelper.getPDPCustomBannerText(props.product as Product)
          }
          productId={props.product?.spid}
        />
      ) : null}
    </>
  )
}

export default GreyBoxesDS
